import { LocationSummaryDto } from '../../location/dtos/location-summary.dto';
import { LocationService } from '../../location/services/location.service';
import { Component, OnInit, ViewChild } from '@angular/core';
import { ErrorService } from '../../../core/services/error.service';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatTable, MatTableModule } from '@angular/material/table';
import { MatIconModule } from '@angular/material/icon';
import { CdkDrag, CdkDragDrop, CdkDropList } from '@angular/cdk/drag-drop';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatDialog } from '@angular/material/dialog';
import { CategoryService } from '../services/category.service';
import { CategoryDto } from '../dtos/category.dto';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatSelectModule } from '@angular/material/select';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { LocalizedTextPipe } from '../../../shared/pipes/localized-text.pipe';
import { ManageCategoryDialog } from '../dialogs/manage-category/manage-category.dialog';
import { ReorderDto } from '../../../shared/dtos/reorder.dto';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { environment } from '../../../../environments/environment';
import { MemoryService } from '../../../core/services/memory.service';
import { MatMenuModule } from '@angular/material/menu';

@Component({
  selector: 'app-categories',
  imports: [MatCardModule, MatButtonModule, MatTableModule, MatIconModule, CdkDropList, CdkDrag, MatFormFieldModule, MatSelectModule, ReactiveFormsModule, MatSnackBarModule, LocalizedTextPipe, TranslocoPipe, MatMenuModule],
  templateUrl: './categories.component.html',
  styleUrl: './categories.component.scss'
})
export class CategoriesComponent implements OnInit {
  @ViewChild('table') table!: MatTable<any>;

  locationSummaryDtos!: LocationSummaryDto[];
  categoryDtos!: CategoryDto[];

  locationFilters!: number[];
  locationFilterFormControl!: FormControl;

  filteredCategories!: CategoryDto[];

  constructor(private locationService: LocationService, private categoryService: CategoryService, private errorService: ErrorService, private dialog: MatDialog, private snackBar: MatSnackBar, private memoryService: MemoryService, private translocoService: TranslocoService) {
  }

  get categoryGroups(): { language: string, values: string[] }[] {
    const categoryGroupsByLanguage = new Map<string, Set<string>>();

    for (const categoryDto of this.categoryDtos ?? []) {
      const localizedTextDtos = categoryDto.categoryGroup ?? [];
      for (const localizedTextDto of localizedTextDtos) {
        if (!localizedTextDto?.language || !localizedTextDto.value) continue;
        const language = localizedTextDto.language.toLowerCase();

        if (!categoryGroupsByLanguage.has(language)) categoryGroupsByLanguage.set(language, new Set<string>());
        categoryGroupsByLanguage.get(language)!.add(localizedTextDto.value);
      }
    }

    return Array.from(categoryGroupsByLanguage.entries()).map(([language, valuesSet]) => ({
      language,
      values: Array.from(valuesSet)
    }));
  }


  ngOnInit(): void {
    this.locationService.getLocationSummaries().subscribe({
      next: (locationPreviewDtos: LocationSummaryDto[]) => {
        this.locationSummaryDtos = locationPreviewDtos;
        this.locationFilters = [-1, 0, ...this.locationSummaryDtos.map((locationSummaryDto) => locationSummaryDto.locationId)];
        this.locationFilterFormControl = new FormControl(-1);

        this.loadCategories();
      },
      error: (error) => {
        this.errorService.handleError(error, this.translocoService.translate("error.title.locations"));
      }
    });
  }

  loadCategories(): void {
    this.categoryService.getCategories().subscribe({
      next: (categoryDtos: CategoryDto[]) => {
        this.categoryDtos = categoryDtos;

        this.filterCategories(this.locationFilterFormControl.value);
      },
      error: (error) => {
        this.errorService.handleError(error, this.translocoService.translate("error.title.categories"));
      }
    });
  }

  filterCategories(filter: number): void {
    if (filter == -1) {
      this.filteredCategories = this.categoryDtos;
      return;
    }

    if (filter == 0) {
      this.filteredCategories = this.categoryDtos.filter((categoryDto) => !categoryDto.locationId);
      return;
    }

    this.filteredCategories = this.categoryDtos.filter((categoryDto) => categoryDto.locationId == filter);
  }

  showLocationPreview(locationId: number): string {
    let locationPreviewDto = this.locationSummaryDtos.find((locationPreviewDto) => locationPreviewDto.locationId == locationId);

    if (!locationPreviewDto) {
      return this.translocoService.translate("label.location.null");
    }

    return `${locationPreviewDto.name} - ${locationPreviewDto.city}, ${locationPreviewDto.street}`;
  }

  add(): void {
    this.dialog
      .open(ManageCategoryDialog, {
        autoFocus: false,
        data: { categoryGroups: this.categoryGroups, locationSummaryDtos: this.locationSummaryDtos }
      })
      .afterClosed()
      .subscribe((reload: boolean) => {
        if (reload) {
          this.loadCategories();
        }
      });
  }

  edit(categoryDto: CategoryDto): void {
    this.dialog
      .open(ManageCategoryDialog, {
        autoFocus: false,
        data: {
          categoryDto: categoryDto,
          categoryGroups: this.categoryGroups,
          locationSummaryDtos: this.locationSummaryDtos
        }
      })
      .afterClosed()
      .subscribe((reload: boolean) => {
        if (reload) {
          this.loadCategories();
        }
      });
  }

  delete(categoryDto: CategoryDto): void {
    this.categoryService.deleteCategory(categoryDto.categoryId).subscribe({
      next: () => {
        this.loadCategories();
      },
      error: (error) => {
        this.errorService.handleError(error, this.translocoService.translate("error.title.delete_category"));
      }
    });
  }

  reorder(event: CdkDragDrop<string>): void {
    let categoryDto: CategoryDto = event.item.data;
    let previousIndex: number = event.previousIndex;
    let newIndex: number = event.currentIndex;

    if (previousIndex === newIndex) {
      return;
    }

    let previousCategoryDto: CategoryDto | null = newIndex === 0 ? null : this.filteredCategories.filter((_, index) => index !== previousIndex)[newIndex - 1];

    let reorderDto: ReorderDto = { objectId: categoryDto.categoryId, previousObjectId: previousCategoryDto?.categoryId ?? null };

    let filteredCategoriesCopy = [...this.filteredCategories];
    const element = this.filteredCategories.splice(previousIndex, 1)[0];
    this.filteredCategories.splice(newIndex, 0, element);
    this.table.renderRows();

    this.categoryService.reorderCategories(reorderDto).subscribe({
      next: () => {
        this.loadCategories();
      },
      error: (error) => {
        this.filteredCategories = filteredCategoriesCopy;
        this.errorService.handleError(error, this.translocoService.translate("error.title.reorder_categories"));
      }
    });
  }

  changeFilter(filter: number): void {
    this.filterCategories(filter);
  }

  locationName(locationId: number): string {
    if (locationId == -1) {
      return this.translocoService.translate("label.location.all");
    }

    if (locationId == 0) {
      return this.translocoService.translate("label.location.general");
    }

    let locationSummaryDto = this.locationSummaryDtos.find((locationPreviewDto) => locationPreviewDto.locationId == locationId);
    return `${locationSummaryDto?.name} - ${locationSummaryDto?.city}, ${locationSummaryDto?.street}`;
  }

  copyUrl(locationId: number, categoryId: number) {
    const url = `${environment.cenplexBookingWebUrl}/appointment-selection?customerId=${this.memoryService.customerId}&locationId=${locationId}&categoryId=${categoryId}`;

    navigator.clipboard.writeText(url).then(() => {
      this.snackBar.open(this.translocoService.translate("label.snackbar"), 'OK', {
        duration: 3000,
        horizontalPosition: 'end',
        verticalPosition: 'bottom'
      });
    });
  }
}
